/**
 * Screen 13 — the nightly cash-up. What was in the till against what the POS
 * says should have been.
 *
 * Above Board does this every night in a Glide app (docs/state.md, "Back up
 * the Glide app"); it was never in the prototype, so the shape here is the
 * one the closing bartender actually works through: count the drawer, read
 * the three tender figures off the POS, read the EFTPOS settlement off the
 * terminal, take the takings out to the safe and leave tomorrow's float.
 *
 * Two rules are worth naming.
 *
 * **Money is integer cents, everywhere.** A drawer count is the one number in
 * BarOS somebody will argue about, so it is never a float. The web converts on
 * the way in and on the way out; nothing in here sees dollars.
 *
 * **The variance bands are the stocktake's**, deliberately. A bar already
 * reads 3 % as normal and 8 % as "something is wrong" from the count sheet,
 * and two scales would mean two conversations. `moneyVariance` calls
 * `variance` from calc.ts rather than restating the thresholds, so they cannot
 * drift apart — with one addition: a few dollars either way on a quiet Tuesday
 * is a rounding of change, not a discrepancy, and a percentage of a small
 * night flags it red. Under `VARIANCE_TOLERANCE_CENTS` is fine regardless.
 */
import { variance } from "./calc.js";
import { isRealDate, serviceDate } from "./prep.js";
import { addDays } from "./time.js";
export const CASHUP_STATUSES = ["draft", "finalised"];
/**
 * Anything at or under this, in cents, is fine whatever the percentage.
 *
 * Three dollars is about the most a drawer drifts from rounding a cash sale
 * and handing back change from a tip jar. Below it the percentage says
 * nothing useful: $2 short on a $180 Tuesday is 1.1 %, and the same $2 on a
 * $40 lunch service is 5 % — the same non-event, banded differently.
 */
export const VARIANCE_TOLERANCE_CENTS = 300;
/**
 * Australian currency, largest first — the order a drawer is counted in.
 *
 * The 1c and 2c coins were withdrawn in 1992 and the 5c is on its way out;
 * it is kept because tills still hold them. A venue that never sees one
 * simply leaves the row at zero.
 */
export const AU_DENOMINATIONS = [
    { cents: 10000, label: "$100", kind: "note" },
    { cents: 5000, label: "$50", kind: "note" },
    { cents: 2000, label: "$20", kind: "note" },
    { cents: 1000, label: "$10", kind: "note" },
    { cents: 500, label: "$5", kind: "note" },
    { cents: 200, label: "$2", kind: "coin" },
    { cents: 100, label: "$1", kind: "coin" },
    { cents: 50, label: "50c", kind: "coin" },
    { cents: 20, label: "20c", kind: "coin" },
    { cents: 10, label: "10c", kind: "coin" },
    { cents: 5, label: "5c", kind: "coin" },
];
/**
 * What a breakdown adds up to, in cents.
 *
 * Anything that is not a finite pair of numbers contributes nothing rather
 * than poisoning the total with a NaN — a half-typed row on the tablet should
 * read as zero, not blank the whole panel.
 */
export function denominationTotal(breakdown) {
    if (!breakdown)
        return 0;
    let total = 0;
    for (const row of breakdown) {
        const face = row?.denominationCents;
        const count = row?.count;
        if (!Number.isFinite(face) || !Number.isFinite(count))
            continue;
        total += Math.round(face) * Math.round(count);
    }
    return total;
}
// ------------------------------------------------------------- the window
/**
 * How many nights the screen and the list route show by default.
 *
 * A fortnight is two of every weeknight, which is what makes a Tuesday's
 * variance readable — one Tuesday tells you nothing.
 */
export const CASHUP_WINDOW_DAYS = 14;
/**
 * The service dates a list covers: whatever the caller asked for, otherwise
 * the last fortnight ending tonight.
 *
 * `to` is a *service* date rather than a calendar one, so a manager checking
 * the till at half past one on Saturday morning is still shown Friday's night
 * as the latest — the same rollover prep uses, for the same reason.
 *
 * A malformed bound is ignored rather than rejected: the default is always a
 * sensible fortnight, and a query string is not a place to fail a read.
 */
export function cashupWindow(from, to, now = new Date()) {
    const end = isRealDate(to) ? to : serviceDate(now);
    const start = isRealDate(from) ? from : addDays(end, -(CASHUP_WINDOW_DAYS - 1));
    return start > end ? { from: end, to: start } : { from: start, to: end };
}
/**
 * A variance in money, on the stocktake's bands plus a small-change floor.
 *
 * The arithmetic is `variance` from calc.ts, called on cents rather than
 * millilitres — the same subtraction and the same 3 % / 8 % thresholds, so a
 * change to the tolerance in one screen reaches both. Only the floor is added
 * here, and only downwards: it can turn a red into a green, never the reverse.
 */
export function moneyVariance(actualCents, expectedCents) {
    const actual = Math.round(actualCents);
    const expected = Math.round(expectedCents);
    const v = variance(actual, expected);
    const deltaCents = Math.round(v.deltaMl);
    const percent = Math.round(v.percent * 100) / 100;
    const band = Math.abs(deltaCents) <= VARIANCE_TOLERANCE_CENTS ? "ok" : v.band;
    return { deltaCents, percent, band };
}
/**
 * The whole reconciliation for one night.
 *
 * Cash and card are separate because they fail for different reasons and
 * different people fix them. A cash variance is a miskeyed tender, a wrong
 * float or a hand in the till; a card variance is a tip adjustment, a refund
 * settled the next day, or a terminal that was not batched — nothing anyone
 * should go looking through the drawer for. Summing them into one figure and
 * only showing that is how a $40 card timing difference gets mistaken for $40
 * of missing cash.
 *
 * `posOtherCents` — vouchers, staff accounts — is in the takings and out of
 * the variances on purpose: there is nothing to count it against.
 */
export function reconcileCashup(record) {
    const expectedCashCents = Math.round(record.floatCents + record.posCashCents);
    const takingsCents = Math.round(record.posCashCents + record.posCardCents + record.posOtherCents);
    const cash = moneyVariance(record.countedCashCents, expectedCashCents);
    const card = moneyVariance(record.terminalCardCents, record.posCardCents);
    const total = moneyVariance(record.countedCashCents + record.terminalCardCents, expectedCashCents + record.posCardCents);
    const drawerDeltaCents = Math.round(record.countedCashCents - record.bankedCents - record.floatRetainedCents);
    return {
        expectedCashCents,
        takingsCents,
        cash,
        card,
        total,
        drawerDeltaCents,
        drawerBalances: drawerDeltaCents === 0,
    };
}
//# sourceMappingURL=cashup.js.map